/**
 * Unified User Management Page
 * Combines Team Management and User Management functionality
 * Displays and manages all admin portal users with invite, edit, suspend/activate, and role management
 */

import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Spinner,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Avatar,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Center,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import {
  MdSearch,
  MdAdd,
  MdRefresh,
  MdMoreVert,
  MdEdit,
  MdBlock,
  MdCheckCircle,
  MdPeople,
  MdPersonAdd,
  MdSecurity,
  MdDelete,
} from 'react-icons/md';
import { collection, query, getDocs, getFirestore, Timestamp } from 'firebase/firestore';
import { useAuth } from '../../../contexts/AuthContext';
import { Permission } from '../../../types/permissions';
import { User } from '../../../types/user';
import {
  getUserStats,
  updateUserStatus,
} from '../../../services/userManagement';
import { getApiUrl } from '../../../utils/apiUrl';
import { ROLE_DISPLAY } from '../../../types/permissions';

interface AuditLog {
  id: string;
  userId: string;
  userName: string;
  action: string;
  targetUserEmail?: string;
  timestamp: any;
  details?: string;
}

export function UserManagement() {
  const toast = useToast();
  const { hasPermission, userProfile, user } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isInviteOpen, onOpen: onInviteOpen, onClose: onInviteClose } = useDisclosure();

  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<any | null>(null);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<string>('admin');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');

  // Edit form state
  const [editRole, setEditRole] = useState<string>('');

  const canManageUsers = hasPermission(Permission.USER_CREATE);
  const cancelRef = useRef<any>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      console.log('[loadData] Starting data load...');

      // Fetch users directly from Firestore
      const db = getFirestore();
      const usersRef = collection(db, 'users');
      console.log('[loadData] Fetching users from Firestore...');
      const usersSnapshot = await getDocs(query(usersRef));
      console.log('[loadData] Got users snapshot:', usersSnapshot.docs.length, 'documents');

      // Also fetch from accountUsers collection (for newly invited users)
      console.log('[loadData] Also checking accountUsers collection...');
      const accountUsersRef = collection(db, 'accountUsers');
      let accountUsersSnapshot;
      try {
        accountUsersSnapshot = await getDocs(query(accountUsersRef));
        console.log('[loadData] Got accountUsers snapshot:', accountUsersSnapshot.docs.length, 'documents');
      } catch (e) {
        console.log('[loadData] Could not fetch accountUsers (may not have permission):', e instanceof Error ? e.message : String(e));
        accountUsersSnapshot = null;
      }

      // Convert users from both collections to User objects
      const firebaseUsers: User[] = [];
      const seenEmails = new Set<string>();

      // Add users from 'users' collection
      usersSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const displayName = data.displayName || data.name || '';
        const [firstName = '', lastName = ''] = displayName.split(' ');
        const email = data.email || '';

        seenEmails.add(email.toLowerCase());
        firebaseUsers.push({
          uid: doc.id,
          email,
          name: displayName,
          role: data.role || 'viewer',
          status: data.status || 'active',
          permissions: data.permissions || [],
          metadata: {
            createdAt: data.createdAt || Timestamp.now(),
            createdBy: data.createdBy || data.invitedBy || 'system',
            lastModifiedAt: data.lastModifiedAt || Timestamp.now(),
            lastModifiedBy: data.lastModifiedBy || 'system',
            lastLogin: data.lastActiveAt,
            loginCount: data.loginCount || 0,
          },
          settings: data.settings || {
            language: 'en',
            timezone: 'America/Toronto',
            notifications: {
              email: true,
              push: true,
              sms: false,
              marketing: false,
            },
          },
        } as User);
      });

      // Add users from 'accountUsers' collection that aren't already in 'users'
      if (accountUsersSnapshot) {
        accountUsersSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const email = (data.email || '').toLowerCase();
          
          // Only add if not already in users collection
          if (!seenEmails.has(email)) {
            const displayName = `${data.firstName || ''} ${data.lastName || ''}`.trim();
            seenEmails.add(email);
            firebaseUsers.push({
              uid: doc.id,
              email: data.email || '',
              name: displayName || data.email || '',
              role: data.role || 'viewer',
              status: data.status || 'pending',
              permissions: data.permissions || [],
              metadata: {
                createdAt: data.createdAt || Timestamp.now(),
                createdBy: data.createdBy || data.invitedBy || 'system',
                lastModifiedAt: data.lastModifiedAt || Timestamp.now(),
                lastModifiedBy: data.lastModifiedBy || 'system',
                lastLogin: data.lastActiveAt,
                loginCount: data.loginCount || 0,
              },
              settings: data.settings || {
                language: 'en',
                timezone: 'America/Toronto',
                notifications: {
                  email: true,
                  push: true,
                  sms: false,
                  marketing: false,
                },
              },
            } as User);
          }
        });
      }

      console.log('[loadData] Setting', firebaseUsers.length, 'users to state (from both collections)');
      setUsers(firebaseUsers);

      // Fetch stats
      console.log('[loadData] Fetching user stats...');
      const statsData = await getUserStats();
      console.log('[loadData] Got stats:', statsData);
      setStats(statsData);

      // Fetch audit logs
      console.log('[loadData] Fetching audit logs...');
      await fetchAuditLogs();
      console.log('[loadData] Data load complete');
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error loading data',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      if (!user) return;

      const token = await user.getIdToken();
      const apiUrl = getApiUrl();
      const response = await fetch(
        `${apiUrl}/getUserAuditLogs?limit=20`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        try {
          const data = await response.json();
          setAuditLogs(data.logs || []);
        } catch (jsonError) {
          console.warn('Invalid JSON response from getUserAuditLogs:', jsonError);
          setAuditLogs([]);
        }
      } else {
        console.warn(`Failed to fetch audit logs: ${response.status} ${response.statusText}`);
        setAuditLogs([]);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      // Silently fail - audit logs are optional
      setAuditLogs([]);
    }
  };

  const handleSuspendUser = async (userId: string) => {
    if (!userProfile) {
      toast({
        title: 'Error',
        description: 'You must be logged in to perform this action',
        status: 'error',
        duration: 5000,
      });
      return;
    }

    try {
      await updateUserStatus(userId, 'suspended', userProfile.uid, 'Suspended by admin');
      toast({
        title: 'User suspended',
        description: 'The user has been suspended successfully',
        status: 'success',
        duration: 3000,
      });
      loadData();
    } catch (error) {
      toast({
        title: 'Error suspending user',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleActivateUser = async (userId: string) => {
    if (!userProfile) {
      toast({
        title: 'Error',
        description: 'You must be logged in to perform this action',
        status: 'error',
        duration: 5000,
      });
      return;
    }

    try {
      await updateUserStatus(userId, 'active', userProfile.uid, 'Activated by admin');
      toast({
        title: 'User activated',
        description: 'The user has been activated successfully',
        status: 'success',
        duration: 3000,
      });
      loadData();
    } catch (error) {
      toast({
        title: 'Error activating user',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditRole(user.role);
    onEditOpen();
  };

  const handleInviteClick = () => {
    setSelectedUser(null);
    setInviteEmail('');
    setInviteFirstName('');
    setInviteLastName('');
    setInviteRole('admin');
    onInviteOpen();
  };

  const handleDeleteClick = (user: User) => {
    setSelectedUser(user);
    onDeleteOpen();
  };

  const inviteUser = async () => {
    if (!inviteEmail || !inviteRole) {
      toast({
        title: 'Validation Error',
        description: 'Email and role are required',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      setProcessing(true);
      console.log('[inviteUser] Starting invite for:', inviteEmail, 'as role:', inviteRole);
      if (!user) throw new Error('Not authenticated');

      const token = await user.getIdToken();
      console.log('[inviteUser] Got auth token, calling API...');
      
      const apiUrl = getApiUrl();
      console.log('[inviteUser] Using API URL:', apiUrl);
      
      const response = await fetch(
        `${apiUrl}/inviteTeamMember`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            email: inviteEmail,
            role: inviteRole,
            firstName: inviteFirstName,
            lastName: inviteLastName,
          }),
        }
      );

      console.log('[inviteUser] API response status:', response.status);
      const responseText = await response.text();
      console.log('[inviteUser] API response raw text:', responseText);
      
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (parseError) {
        console.error('[inviteUser] Failed to parse JSON:', parseError);
        console.error('[inviteUser] Raw response was:', responseText.substring(0, 500));
        throw new Error(`Invalid response from server (status ${response.status}): ${responseText.substring(0, 200)}`);
      }
      
      console.log('[inviteUser] API response data:', responseData);
      
      if (!response.ok) {
        const error = responseData;
        console.error('[inviteUser] API error:', error);
        throw new Error(error.message || error.error || 'Failed to invite user');
      }

      console.log('[inviteUser] Invitation successful!');
      
      toast({
        title: 'Invitation Sent',
        description: `Successfully invited ${inviteEmail} as ${inviteRole}.`,
        status: 'success',
        duration: 5000,
      });

      console.log('[inviteUser] Closing modal and calling loadData...');
      onInviteClose();
      console.log('[inviteUser] Modal closed, calling loadData immediately...');
      
      // Backend now writes to users collection immediately, so no delay needed
      console.log('[inviteUser] Backend writes directly to users collection');
      loadData();
      console.log('[inviteUser] loadData called (async, will complete in background)');
    } catch (error) {
      console.error('[inviteUser] Caught error:', error);
      toast({
        title: 'Invitation Failed',
        description: error instanceof Error ? error.message : 'Failed to invite user.',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setProcessing(false);
    }
  };

  const deleteUser = async () => {
    if (!selectedUser || !userProfile) {
      toast({
        title: 'Error',
        description: 'User not selected',
        status: 'error',
        duration: 5000,
      });
      return;
    }

    try {
      setProcessing(true);
      if (!user) throw new Error('Not authenticated');

      const token = await user.getIdToken();
      const apiUrl = getApiUrl();
      const response = await fetch(
        `${apiUrl}/removeTeamMember`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            memberId: selectedUser.uid,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to remove user');
      }

      toast({
        title: 'User Removed',
        description: `Successfully removed ${selectedUser.email}.`,
        status: 'success',
        duration: 3000,
      });

      onDeleteClose();
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to remove user.',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setProcessing(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = !roleFilter || user.role === roleFilter;
    const matchesStatus = !statusFilter || user.status === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Never';
    try {
      return timestamp.toDate().toLocaleDateString('en-CA', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return 'Invalid date';
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'superadmin':
        return 'red';
      case 'admin':
        return 'purple';
      case 'sales':
        return 'blue';
      case 'support':
        return 'green';
      case 'accountant':
        return 'orange';
      default:
        return 'gray';
    }
  };

  if (loading && !users.length) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <VStack spacing="20px" align="stretch">
        {/* Statistics Cards */}
        {stats && (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap="20px">
            <Card>
              <CardBody>
                <Stat>
                  <HStack mb="2">
                    <Icon as={MdPeople} boxSize="6" color="blue.500" />
                    <StatLabel>Total Users</StatLabel>
                  </HStack>
                  <StatNumber>{stats.totalUsers}</StatNumber>
                  <StatHelpText>
                    New this month: {stats.newThisMonth}
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <Stat>
                  <HStack mb="2">
                    <Icon as={MdCheckCircle} boxSize="6" color="green.500" />
                    <StatLabel>Active Users</StatLabel>
                  </HStack>
                  <StatNumber>{stats.activeUsers}</StatNumber>
                  <StatHelpText>
                    {((stats.activeUsers / stats.totalUsers) * 100).toFixed(0)}% of total
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <Stat>
                  <HStack mb="2">
                    <Icon as={MdBlock} boxSize="6" color="red.500" />
                    <StatLabel>Suspended</StatLabel>
                  </HStack>
                  <StatNumber>{stats.suspendedUsers}</StatNumber>
                  <StatHelpText>Inactive accounts</StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <Stat>
                  <HStack mb="2">
                    <Icon as={MdSecurity} boxSize="6" color="purple.500" />
                    <StatLabel>Admins</StatLabel>
                  </HStack>
                  <StatNumber>
                    {stats.byRole.superadmin + stats.byRole.admin}
                  </StatNumber>
                  <StatHelpText>
                    Super: {stats.byRole.superadmin}
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </SimpleGrid>
        )}

        {/* Main Card */}
        <Card>
          <CardHeader>
            <Flex justify="space-between" align="center" mb="4">
              <Heading size="md">User Management</Heading>
              <HStack>
                <IconButton
                  aria-label="Refresh"
                  icon={<MdRefresh />}
                  onClick={loadData}
                  isLoading={loading}
                />
                {canManageUsers && (
                  <Button
                    leftIcon={<MdAdd />}
                    colorScheme="brand"
                    onClick={handleInviteClick}
                  >
                    Invite User
                  </Button>
                )}
              </HStack>
            </Flex>

            {/* Filters */}
            <HStack spacing="4" mb="4">
              <InputGroup maxW="400px">
                <InputLeftElement>
                  <Icon as={MdSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>

              <Select
                maxW="200px"
                placeholder="All Roles"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <option value="superadmin">Superadmin</option>
                <option value="admin">Admin</option>
                <option value="sales">Sales</option>
                <option value="support">Support</option>
                <option value="accountant">Accountant</option>
              </Select>

              <Select
                maxW="200px"
                placeholder="All Statuses"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </Select>
            </HStack>
          </CardHeader>

          <CardBody>
            {filteredUsers.length === 0 ? (
              <Flex direction="column" align="center" justify="center" py="12">
                <Icon as={MdPersonAdd} boxSize="12" color="gray.300" mb="4" />
                <Text color="gray.500">No users found</Text>
              </Flex>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>User</Th>
                      <Th>Email</Th>
                      <Th>Role</Th>
                      <Th>Status</Th>
                      <Th>Permissions</Th>
                      <Th>Created</Th>
                      <Th>Last Login</Th>
                      <Th></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredUsers.map((user) => (
                      <Tr key={user.uid} _hover={{ bg: 'gray.50' }}>
                        <Td>
                          <HStack>
                            <Avatar size="sm" name={user.name} />
                            <VStack align="start" spacing="0">
                              <Text fontWeight="bold">{user.name}</Text>
                            </VStack>
                          </HStack>
                        </Td>
                        <Td>
                          <Text fontSize="sm">{user.email}</Text>
                        </Td>
                        <Td>
                          <Badge colorScheme={getRoleBadgeColor(user.role)}>
                            {ROLE_DISPLAY[user.role]?.name || user.role}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={user.status === 'active' ? 'green' : 'red'}
                          >
                            {user.status}
                          </Badge>
                        </Td>
                        <Td>
                          <Text fontSize="sm">
                            {user.permissions.length} permissions
                          </Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm">
                            {formatDate(user.metadata.createdAt)}
                          </Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm">
                            {formatDate(user.metadata.lastLogin)}
                          </Text>
                        </Td>
                        <Td>
                          {canManageUsers && (
                            <Menu>
                              <MenuButton
                                as={IconButton}
                                icon={<MdMoreVert />}
                                variant="ghost"
                                size="sm"
                              />
                              <MenuList>
                                <MenuItem
                                  icon={<MdEdit />}
                                  onClick={() => handleEditUser(user)}
                                >
                                  Edit User
                                </MenuItem>
                                {user.status === 'active' ? (
                                  <MenuItem
                                    icon={<MdBlock />}
                                    onClick={() => handleSuspendUser(user.uid)}
                                    color="red.500"
                                  >
                                    Suspend User
                                  </MenuItem>
                                ) : (
                                  <MenuItem
                                    icon={<MdCheckCircle />}
                                    onClick={() => handleActivateUser(user.uid)}
                                    color="green.500"
                                  >
                                    Activate User
                                  </MenuItem>
                                )}
                                <MenuItem
                                  icon={<MdDelete />}
                                  onClick={() => handleDeleteClick(user)}
                                  color="red.600"
                                >
                                  Remove User
                                </MenuItem>
                              </MenuList>
                            </Menu>
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </CardBody>
        </Card>

        {/* Audit Logs */}
        {auditLogs.length > 0 && (
          <Card>
            <CardHeader>
              <Heading size="md">Recent Activity</Heading>
            </CardHeader>
            <CardBody>
              <VStack align="start" spacing="4">
                {auditLogs.slice(0, 10).map((log) => (
                  <HStack key={log.id} justify="space-between" w="full">
                    <VStack align="start" spacing="1">
                      <Text fontWeight="bold" fontSize="sm">
                        {log.action}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {log.userName} • {log.targetUserEmail}
                      </Text>
                    </VStack>
                    <Text fontSize="xs" color="gray.400">
                      {formatDate(log.timestamp)}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </CardBody>
          </Card>
        )}
      </VStack>

      {/* Invite Modal */}
      <Modal isOpen={isInviteOpen} onClose={onInviteClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Invite New User</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing="4">
              <FormControl isRequired>
                <FormLabel>Email Address</FormLabel>
                <Input
                  type="email"
                  placeholder="user@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel>First Name</FormLabel>
                <Input
                  placeholder="First name"
                  value={inviteFirstName}
                  onChange={(e) => setInviteFirstName(e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Last Name</FormLabel>
                <Input
                  placeholder="Last name"
                  value={inviteLastName}
                  onChange={(e) => setInviteLastName(e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Role</FormLabel>
                <Select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                >
                  <option value="superadmin">Superadmin</option>
                  <option value="admin">Admin</option>
                  <option value="sales">Sales</option>
                  <option value="support">Support</option>
                  <option value="accountant">Accountant</option>
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onInviteClose}>
              Cancel
            </Button>
            <Button
              colorScheme="brand"
              isLoading={processing}
              onClick={inviteUser}
            >
              Send Invitation
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Remove User
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to remove <strong>{selectedUser?.email}</strong>? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={deleteUser}
                ml={3}
                isLoading={processing}
              >
                Remove
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}

export default UserManagement;
